import { useState } from 'react';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BRAND } from '@/config/brand';
import { scrollToId } from '@/lib/scroll';
import { trackEvent } from '@/lib/analytics';

const LINKS = [
  { label: 'Home', href: '#home' },
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'Our Work', to: '/portfolio' },
  { label: 'Why AJ', href: '#why-aj' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'Contact', href: '#contact' },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, 'change', (v) => setScrolled(v > 40));

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <>
      <motion.header
        data-testid="main-header"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? 'bg-black/60 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button
            data-testid="nav-brand"
            onClick={() => go('#home')}
            className="font-serif text-xl sm:text-2xl tracking-wide text-cream"
          >
            Events <span className="italic text-champagne">by</span> AJ
          </button>

          <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop">
            {LINKS.map((l) =>
              l.to ? (
                <Link
                  key={l.label}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, '-')}`}
                  to={l.to}
                  className="text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-champagne transition-colors duration-300"
                >
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.label}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => go(l.href)}
                  className="text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-champagne transition-colors duration-300"
                >
                  {l.label}
                </button>
              )
            )}
            <Link data-testid="nav-link-pricing" to="/pricing" className="text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-champagne transition-colors duration-300">
              Pricing
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-enquire-btn"
              onClick={() => { trackEvent('enquire_click', { location: 'navbar' }); go('#enquiry'); }}
              className="hidden sm:inline-flex items-center border border-champagne/60 text-champagne px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] hover:bg-champagne hover:text-ink transition-colors duration-300"
            >
              Enquire Now
            </button>
            <button
              data-testid="nav-menu-btn"
              onClick={() => setOpen(true)}
              className="lg:hidden text-cream p-2"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-ink/95 backdrop-blur-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
              <span className="font-serif text-xl text-cream">Events <span className="italic text-champagne">by</span> AJ</span>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-cream p-2" aria-label="Close menu">
                <X size={24} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) =>
                l.to ? (
                  <motion.div
                    key={l.label}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 * i, duration: 0.5 }}
                  >
                    <Link
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, '-')}`}
                      to={l.to}
                      onClick={() => setOpen(false)}
                      className="block font-serif text-4xl font-light text-cream py-3 border-b border-white/5 hover:text-champagne transition-colors duration-300"
                    >
                      {l.label}
                    </Link>
                  </motion.div>
                ) : (
                  <motion.button
                    key={l.label}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, '-')}`}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 * i, duration: 0.5 }}
                    onClick={() => go(l.href)}
                    className="text-left font-serif text-4xl font-light text-cream py-3 border-b border-white/5 hover:text-champagne transition-colors duration-300"
                  >
                    {l.label}
                  </motion.button>
                )
              )}
              <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.45, duration: 0.5 }}>
                <Link data-testid="mobile-nav-pricing" to="/pricing" onClick={() => setOpen(false)} className="block font-serif text-4xl font-light text-champagne py-3 border-b border-white/5">
                  Pricing
                </Link>
              </motion.div>
              <motion.button
                data-testid="mobile-enquire-btn"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                onClick={() => { trackEvent('enquire_click', { location: 'mobile_menu' }); go('#enquiry'); }}
                className="btn-gold mt-8"
              >
                Enquire Now
              </motion.button>
            </nav>
            <p className="px-8 pb-8 text-xs uppercase tracking-[0.3em] text-white/30">{BRAND.city}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
